import React, { useEffect, useState } from "react";
import {
  Bell,
  Mail,
  Info,
  IndianRupee,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X,
} from "lucide-react";
import { setLowBalanceAlert } from "../../Services/profile/balanceAlert";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { fetchProfile } from "../../Services/profile/profileservice";

const LowBalanceAlertModal = ({ isOpen, onClose }) => {
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // 'idle' | 'loading' | 'success' | 'failed'
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    if (!isOpen) return; // Only fetch if modal is open

    const loadProfile = async () => {
      try {
        const res = await fetchProfile();
        setProfileData(res?.data || null);
        if (res?.data?.email_address) {
          setEmail(res?.data?.email_address);
        }
      } catch (error) {
        console.error(error);
        showErrorToast("Failed to fetch profile");
      }
    };

    loadProfile();
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount) return showErrorToast("Please enter Amount");

    try {
      setStatus("loading");

      const res = await setLowBalanceAlert({
        alert_amount: amount,
        email,
      });

      if (res?.status?.code === 200) {
        showSuccessToast(res?.data?.message || res?.message);
        setStatus("success");
        // Optional: Close modal automatically after success
        // setTimeout(() => onClose(), 2000); 
      } else {
        showErrorToast(res?.data?.message || res?.message);
        setStatus("failed");
      }
    } catch (error) {
      console.log(error);
      setStatus("failed");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity">
      {/* Modal Container */}
      <div className="relative w-full max-w-xl bg-white border border-blue-100 rounded-[2.5rem] shadow-2xl p-6 sm:p-8 z-10 animate-in fade-in zoom-in-95 duration-200">
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header Icon & Title */}
        <div className="flex flex-col items-center mb-8 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-[0_10px_20px_-10px_rgba(59,130,246,0.8)] mb-5 transform rotate-3 transition-transform hover:rotate-6">
            <Bell
              className="w-7 h-7 text-white transform -rotate-3"
              strokeWidth={2.5}
            />
          </div>
          <h2 className="text-3xl font-extrabold text-slate-800 tracking-tight">
            Set Alert
          </h2>
          <p className="text-slate-500 mt-2 font-medium text-sm">
            We'll notify you instantly
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
         
          {/* Alert Amount Input (Mandatory) */}
          <div className="space-y-2 group">
            <label
              htmlFor="amount"
              className="block text-sm font-bold text-slate-700 ml-1"
            >
              Alert Amount <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 transition-colors group-focus-within:text-blue-600">
                <IndianRupee className="w-5 h-5 text-slate-800 group-focus-within:text-blue-600 transition-colors" />
              </div>
              <input
                type="number"
                id="amount"
                required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full pl-11 pr-4 py-3.5 bg-slate-50/50 border border-slate-200 focus:border-blue-400 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all shadow-[inset_0_2px_5px_rgba(0,0,0,0.02)] text-slate-800 font-bold text-lg placeholder:text-slate-400 placeholder:font-medium"
              />
            </div>
          </div>

          {/* Alternate Email Input (Optional) */}
          <div className="space-y-2 group">
            <label
              htmlFor="email"
              className="block text-sm font-bold text-slate-700 ml-1"
            >
              Custom Email{" "}
              <span className="text-slate-400 font-medium text-xs ml-1">
                (Optional)
              </span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 transition-colors group-focus-within:text-blue-600">
                <Mail className="w-5 h-5" />
              </div>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter new email address"
                className="w-full pl-11 pr-4 py-3.5 bg-slate-50/50 border border-slate-200 focus:border-blue-400 rounded-2xl outline-none focus:ring-4 focus:ring-blue-500/10 transition-all shadow-[inset_0_2px_5px_rgba(0,0,0,0.02)] text-slate-800 font-medium placeholder:text-slate-400"
              />
            </div>
          </div>

           {/* Instruction Box */}
          <div className="flex items-start gap-3 p-4 bg-blue-50/50 border border-blue-100 rounded-2xl backdrop-blur-md shadow-sm">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600 leading-relaxed font-medium">
              The alert will be sent to your{" "}
              <span className="text-blue-900 font-bold">
                registered email ID
              </span>
              . If you want to change above for alert only.
            </p>
          </div>

          {/* Dynamic API Response Messages */}
          <div className="min-h-[44px]">
            {status === "success" && (
              <div className="flex items-center justify-center gap-2 text-emerald-700 bg-emerald-50/80 border border-emerald-200/60 py-2.5 px-4 rounded-xl shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-bold">
                  Alert configured successfully!
                </span>
              </div>
            )}

            {status === "failed" && (
              <div className="flex items-center justify-center gap-2 text-rose-700 bg-rose-50/80 border border-rose-200/60 py-2.5 px-4 rounded-xl shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm font-bold">
                  Failed to set alert. Please try again.
                </span>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === "loading"}
            className="w-full relative group overflow-hidden rounded-2xl disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <div className="relative flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 px-8 rounded-2xl font-extrabold text-[15px] shadow-[0_8px_20px_-8px_rgba(37,99,235,0.6)] transition-all group-hover:shadow-[0_10px_25px_-8px_rgba(37,99,235,0.8)] active:scale-[0.98]">
              {status === "loading" ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                "Set Alert"
              )}
            </div>
          </button>
        </form>
      </div>
    </div>
  );
};

export default LowBalanceAlertModal;